import { API_SOCKET_URL } from '@/constants'
import { useNotificationStore, useTokenStore } from '@/store'
import { INotification } from '@/types'
import { useToast } from 'jobseeker-ui'
import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { Socket, io } from 'socket.io-client'

interface ISocketContext {
  isConnected: boolean
}

const SocketContext = createContext<ISocketContext | undefined>(undefined)

export const useSocket = (): ISocketContext => {
  const context = useContext(SocketContext)
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider')
  }
  return context
}

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false)
  const socketRef = useRef<Socket | null>(null)
  const { refreshAccessToken } = useTokenStore.getState()
  const notifications = useNotificationStore()
  const toast = useToast()

  // Function to create a new Socket.IO connection
  const createSocket = async () => {
    try {
      const { access_token } = await refreshAccessToken()
      if (!access_token) return
      // Initialize Socket.IO with auth and reconnection options
      const socket = io(API_SOCKET_URL, {
        auth: {
          token: access_token,
          'source-app': window.__APP__.SOURCE_APP,
        },
        extraHeaders: {
          authorization: access_token,
        },
      })

      // Connection events
      socket.on('connect', () => {
        setIsConnected(true)
      })

      socket.on('disconnect', () => {
        setIsConnected(false)
      })

      // Handle incoming notifications
      socket.on('notification', (data: INotification) => {
        try {
          toast(data.message || '')
          notifications.incoming(data)
        } catch (error) {
          console.error('Error processing notification:', error)
        }
      })

      // Error handling
      socket.on('connect_error', (error) => {
        console.error('Connection error:', error)
        setIsConnected(false)
      })

      socket.on('error', (error) => {
        console.error('Socket error:', error)
        socket.disconnect()
      })

      // Store socket reference
      socketRef.current = socket

      return () => {
        socket.disconnect()
      }
    } catch (error) {
      console.error('Error creating socket connection:', error)
      setIsConnected(false)
    }
  }

  // Initialize Socket.IO connection
  useEffect(() => {
    let cleanup: (() => void) | undefined

    const initSocket = async () => {
      cleanup = await createSocket()
    }

    initSocket()

    // Cleanup on unmount
    return () => {
      cleanup?.()
      socketRef.current?.disconnect()
      socketRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <SocketContext.Provider value={{ isConnected }}>{children}</SocketContext.Provider>
}
